/**
 * Pure barcode logic (CC-6: NEVER use Lingui macros here).
 *
 * Supports input via HID reader (keyboard: emits characters + Enter) and, in
 * the UI, manual input. Works offline: resolution is 100% local against the
 * already synchronized catalog. Without network or camera dependencies.
 */

export interface ProductLike {
  id: string;
  sku?: string | null;
  barcode?: string | null;
  name?: string | null;
}

/**
 * ASCII control characters (0x00–0x1F and 0x7F) that an HID scanner can emit
 * (e.g. Enter \r/\n at the end of the scan). Built with `new RegExp`
 * to avoid embedding control bytes in source code.
 */
const CONTROL_CHARS = new RegExp(String.raw`[\u0000-\u001F\u007F]`, "g");

/** Normalizes a scanned code: trims whitespace and control characters. */
export function normalizeBarcode(raw: string): string {
  return raw.replace(CONTROL_CHARS, "").trim();
}

/** Does code appear valid for lookup? (non-empty after normalization). */
export function isScannableCode(raw: string): boolean {
  return normalizeBarcode(raw).length > 0;
}

/**
 * Looks up a product by barcode (exact match) and, as fallback,
 * by SKU. Returns null if there is no match. Deterministic and case-sensitive
 * for barcode (EAN/UPC are numeric); SKU is compared case-insensitively
 * to tolerate manual typing.
 */
export function findProductByCode<T extends ProductLike>(products: T[], rawCode: string): T | null {
  const code = normalizeBarcode(rawCode);
  if (code === "") {
    return null;
  }
  const byBarcode = products.find((p) => (p.barcode ?? "") === code);
  if (byBarcode) {
    return byBarcode;
  }
  const lowered = code.toLowerCase();
  const bySku = products.find((p) => (p.sku ?? "").toLowerCase() === lowered);
  return bySku ?? null;
}
